/**
 * Ad placement manager.
 * Only loads advertisements when consent is granted, config is present,
 * and the placement is on an allow-list. Does not load ads on empty,
 * transient or low-content screens.
 */

export type AdPosition = 'sidebar' | 'below-instructions' | 'before-related' | 'inline-guide';

const BLOCKED_PATHS = ['/404', '/contact', '/cookies', '/privacy', '/terms', '/accessibility'];
const BLOCKED_POSITIONS_ON_GAME_LOAD = ['below-instructions'];

function hasConsent(): boolean {
  try {
    return localStorage.getItem('chance-consent') === 'all';
  } catch {
    return false;
  }
}

function isConfigValid(): boolean {
  const config = typeof window !== 'undefined' ? window.CHANCE_CONFIG : undefined;
  return Boolean(config && config.adsenseClient && (config.sideAdSlot || config.bottomAdSlot));
}

function isAdAllowed(position: AdPosition): boolean {
  if (!hasConsent()) return false;
  if (!isConfigValid()) return false;

  const path = location.pathname;
  if (BLOCKED_PATHS.some((blocked) => path.startsWith(blocked))) return false;

  // Do not show ads while a game tool is still in its initial empty state.
  const gameMount = document.querySelector('[data-game]');
  const isGameLoaded = gameMount
    ? gameMount.querySelector('.game-mount-inner, .team-generator, .probability-lab') !== null
    : false;
  if (!isGameLoaded && BLOCKED_POSITIONS_ON_GAME_LOAD.includes(position)) return false;

  return true;
}

function loadAdSenseScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[data-google-adsense]')) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.dataset.googleAdsense = 'true';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(
      window.CHANCE_CONFIG?.adsenseClient || ''
    )}`;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
}

export function initAds() {
  if (!hasConsent() || !isConfigValid()) {
    document.querySelectorAll<HTMLElement>('[data-ad-position]').forEach((el) => (el.hidden = true));
    return;
  }

  loadAdSenseScript()
    .then(() => {
      const placeholders = document.querySelectorAll<HTMLElement>('[data-ad-position]');
      placeholders.forEach((placeholder) => {
        const position = placeholder.dataset.adPosition as AdPosition;
        if (!isAdAllowed(position)) {
          placeholder.hidden = true;
          return;
        }
        placeholder.classList.remove('ad-placeholder');
        placeholder.classList.add('ad-container');
        placeholder.innerHTML = `
          <span class="ad-label">Advertisement</span>
          <ins class="adsbygoogle" style="display:block" data-ad-client="${window.CHANCE_CONFIG?.adsenseClient}" data-ad-slot="${
          position === 'sidebar' ? window.CHANCE_CONFIG?.sideAdSlot : window.CHANCE_CONFIG?.bottomAdSlot
        }" data-ad-format="auto" data-full-width-responsive="true"></ins>
        `;
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch {
          placeholder.hidden = true;
        }
      });
    })
    .catch(() => {
      document.querySelectorAll<HTMLElement>('[data-ad-position]').forEach((el) => (el.hidden = true));
    });
}

// Listen for consent changes and re-initialise.
window.addEventListener('chance-consent-updated', () => initAds());

// Re-check ad eligibility when a game finishes mounting.
const gameMount = document.querySelector('[data-game]');
if (gameMount && typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver(() => initAds());
  observer.observe(gameMount, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAds);
} else {
  initAds();
}
